import { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Wand2, Database, Search, PlusCircle, SkipForward, ChevronLeft } from "lucide-react";

const UNITS = ["ml", "l", "g", "kg", "pz"];

export function ResolveIngredient({ standardRecipe, ingredientName, onResolved }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("menu");
  const [busy, setBusy] = useState(false);
  const [masters, setMasters] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [q, setQ] = useState(ingredientName || "");
  const [cf, setCf] = useState({ canonical_name: ingredientName || "", category: "", base_unit: "ml", brand: "", ean: "" });

  const reset = () => { setView("menu"); setMasters([]); setPurchases([]); setQ(ingredientName || ""); setCf({ canonical_name: ingredientName || "", category: "", base_unit: "ml", brand: "", ean: "" }); };

  const resolve = async (ingredient_id) => {
    setBusy(true);
    try {
      await api.post("/recipes/resolutions", { standard_recipe: standardRecipe, ingredient_name: ingredientName, ingredient_id });
      toast.success(ingredient_id ? "Ingrediente collegato" : "Lasciato in sospeso");
      setOpen(false); reset(); onResolved && onResolved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const searchMasters = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/fc/ingredients?active=true&search=${encodeURIComponent(q)}`); setMasters(data); }
    catch { setMasters([]); } finally { setBusy(false); }
  };
  const searchPurchases = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/recipes/search-purchases?query=${encodeURIComponent(q)}`); setMasters(data.masters || []); setPurchases(data.purchases || []); }
    catch { setMasters([]); setPurchases([]); } finally { setBusy(false); }
  };
  const createMaster = async () => {
    if (!cf.canonical_name.trim()) return toast.error("Nome canonico obbligatorio");
    setBusy(true);
    try {
      const { data } = await api.post("/fc/ingredients", { canonical_name: cf.canonical_name.trim(), category: cf.category, base_unit: cf.base_unit, brand: cf.brand, ean: cf.ean, supplier_aliases: [], active: true });
      await resolve(data.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  const Back = () => <button onClick={() => setView("menu")} className="inline-flex items-center text-xs text-muted-foreground mb-2"><ChevronLeft className="w-3.5 h-3.5" />indietro</button>;

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <PopoverTrigger asChild>
        <button className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20" data-testid={`resolve-btn-${(ingredientName || "").toLowerCase()}`}><Wand2 className="w-3 h-3" />Risolvi</button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-3" align="start">
        <p className="text-xs text-muted-foreground mb-2">Risolvi <b className="text-foreground">{ingredientName}</b> in <b className="text-foreground">{standardRecipe}</b></p>
        {view === "menu" && (
          <div className="space-y-1.5">
            <Button variant="outline" size="sm" className="w-full justify-start rounded-lg" onClick={() => { setView("master"); searchMasters(); }} data-testid="resolve-select-master"><Database className="w-4 h-4 mr-2" />Seleziona master esistente</Button>
            <Button variant="outline" size="sm" className="w-full justify-start rounded-lg" onClick={() => { setView("purchase"); searchPurchases(); }} data-testid="resolve-search-purchases"><Search className="w-4 h-4 mr-2" />Cerca negli acquisti/fatture</Button>
            <Button variant="outline" size="sm" className="w-full justify-start rounded-lg" onClick={() => setView("create")} data-testid="resolve-create-master"><PlusCircle className="w-4 h-4 mr-2" />Crea nuovo master</Button>
            <Button variant="ghost" size="sm" className="w-full justify-start rounded-lg" onClick={() => resolve("")} data-testid="resolve-pending"><SkipForward className="w-4 h-4 mr-2" />Lascia pending</Button>
          </div>
        )}
        {view === "master" && (
          <div><Back />
            <div className="flex gap-1 mb-2"><Input value={q} onChange={(e) => setQ(e.target.value)} className="h-8" placeholder="cerca master…" /><Button size="sm" className="h-8" onClick={searchMasters} disabled={busy}>Cerca</Button></div>
            <div className="max-h-48 overflow-auto space-y-1">
              {masters.length === 0 && <p className="text-xs text-muted-foreground py-2 text-center">Nessun master</p>}
              {masters.map((m) => (
                <button key={m.ingredient_id || m.id} onClick={() => resolve(m.ingredient_id || m.id)} disabled={busy} className="w-full text-left text-sm px-2 py-1.5 rounded-lg hover:bg-muted" data-testid={`resolve-master-${m.ingredient_id || m.id}`}>{m.name || m.canonical_name} <span className="text-xs text-muted-foreground">{m.base_unit}</span></button>
              ))}
            </div>
          </div>
        )}
        {view === "purchase" && (
          <div><Back />
            <div className="flex gap-1 mb-2"><Input value={q} onChange={(e) => setQ(e.target.value)} className="h-8" placeholder="cerca in fatture…" /><Button size="sm" className="h-8" onClick={searchPurchases} disabled={busy}>Cerca</Button></div>
            <div className="max-h-56 overflow-auto space-y-2">
              {masters.length > 0 && <div><p className="text-[11px] uppercase text-muted-foreground">Master collegabili</p>{masters.map((m) => <button key={m.ingredient_id} onClick={() => resolve(m.ingredient_id)} className="w-full text-left text-sm px-2 py-1 rounded-lg hover:bg-muted" data-testid={`resolve-pm-${m.ingredient_id}`}>{m.name}</button>)}</div>}
              <div>
                <p className="text-[11px] uppercase text-muted-foreground">Candidati reali in fattura</p>
                {purchases.length === 0 && <p className="text-xs text-muted-foreground py-1">Nessun candidato reale</p>}
                {purchases.map((p, idx) => (
                  <div key={idx} className="text-xs border border-border rounded-lg p-2 mt-1" data-testid={`resolve-purchase-${idx}`}>
                    <p className="font-medium text-foreground">{p.description}</p>
                    <p className="text-muted-foreground">VAT {p.supplier_vat || "—"} · code {p.supplier_product_code || "—"} · EAN {p.ean || "—"} · ×{p.count}</p>
                    {p.resolved_ingredient_id
                      ? <Button size="sm" variant="outline" className="h-7 mt-1 rounded-full text-xs" onClick={() => resolve(p.resolved_ingredient_id)}>Collega master</Button>
                      : <Button size="sm" variant="outline" className="h-7 mt-1 rounded-full text-xs" onClick={() => { setCf((c) => ({ ...c, canonical_name: p.description, ean: p.ean || "" })); setView("create"); }}>Crea master da questo</Button>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {view === "create" && (
          <div className="space-y-2"><Back />
            <div className="space-y-1"><Label className="text-xs">Nome canonico *</Label><Input value={cf.canonical_name} onChange={(e) => setCf({ ...cf, canonical_name: e.target.value })} className="h-8" data-testid="resolve-cf-name" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label className="text-xs">Categoria</Label><Input value={cf.category} onChange={(e) => setCf({ ...cf, category: e.target.value })} className="h-8" /></div>
              <div className="space-y-1"><Label className="text-xs">Unità base</Label><Select value={cf.base_unit} onValueChange={(v) => setCf({ ...cf, base_unit: v })}><SelectTrigger className="h-8" data-testid="resolve-cf-unit"><SelectValue /></SelectTrigger><SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label className="text-xs">Marca</Label><Input value={cf.brand} onChange={(e) => setCf({ ...cf, brand: e.target.value })} className="h-8" /></div>
              <div className="space-y-1"><Label className="text-xs">EAN</Label><Input value={cf.ean} onChange={(e) => setCf({ ...cf, ean: e.target.value })} className="h-8" /></div>
            </div>
            <p className="text-[11px] text-amber-600">Nessun costo verrà inventato: finché non arriva una fattura reale il costo resta NON DISPONIBILE.</p>
            <Button size="sm" className="w-full rounded-full" onClick={createMaster} disabled={busy} data-testid="resolve-cf-save">Crea e collega</Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
